import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Form, Field } from 'react-final-form'

import {
  useCreateFeedbackMutation,
} from 'api/feedback'

import { usePrevious } from 'utils/hooks'

import * as feedbackSelector from 'selectors/feedback'
import { hideSidebarForm } from 'reducers/feedback'

import RightSidebar from 'components/Shared/Sidebar/RightSidebar'
import IconLink from 'components/Shared/IconLink/Index'
import SidebarSection from 'components/Shared/Sidebar/SidebarSection/Index'
import SidebarSectionFormSubmit from 'components/Shared/Sidebar/SidebarSectionFormSubmit/Index'
import DynamicTextArea from 'components/Shared/DynamicTextArea/Index'
import EmoSwitcher from 'components/Shared/EmoSwitcher/Index'

import colors from 'components/utils/colors'
import HeaderBackground from 'assets/img/feedback_header.png'

const FeedbackPanelWrapper = styled.div`
  display: flex;
  position: absolute;
  flex: 1;
  overflow: hidden;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const StyledCloseButton = styled(IconLink)`
  position: absolute;
  right: 14px;
  top: 18px;
`

const StyledHeader = styled.div`
  position: relative;
  color: ${colors.secondary20};
  border-bottom: 1px solid ${colors.secondary70};
  padding: 14px 0px 12px 16px;
  background-image: url(${HeaderBackground});
`

const Title = styled.h4`
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
`

const Subtitle = styled.div`
  font-size: 12px;
  margin-left: 2px;
  margin-bottom: 4px;
`

/* eslint-disable react/prop-types */
const EmoSwitcherAdapter = ({ input }) => (
  <EmoSwitcher
    value={input.value}
    onChange={input.onChange}
  />
)

const DynamicTextAreaAdapter = ({ input, domNodeRef }) => (
  <DynamicTextArea
    small
    value={input.value}
    placeholder=''
    onChange={input.onChange}
    minRows={7}
    domNodeRef={domNodeRef}
  />
)
/* eslint-enable react/prop-types */

const requiredValidator = (value) => {
  const parsedValue = (typeof value === 'string') ? value.trim() : value
  return (parsedValue ? undefined : 'Required')
}


const FeedbackSidebarForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const formRef = useRef(null)
  const textareaRef = useRef(null)

  const isFeedbackSidebarFormVisible = useSelector(feedbackSelector.isFeedbackSidebarFormVisible)
  const isFeedbackSidebarFormVisiblePrev = usePrevious(isFeedbackSidebarFormVisible)
  const isBecomeVisible = Boolean(isFeedbackSidebarFormVisible && !isFeedbackSidebarFormVisiblePrev)

  const [createFeedback, { isLoading: isCreating }] = useCreateFeedbackMutation()

  const focusTextArea = () => {
    // setting focus withoug setTimeout breaks slide animation
    setTimeout(() => {
      textareaRef.current?.focus()
    }, 400)
  }

  const onSubmit = (values) => {
    const params = Object.assign(values, { origin_location: window.location.href })

    createFeedback(params).then(fulfilled => {
      if (!fulfilled.data?.error) {
        formRef.current?.reset()
        dispatch(hideSidebarForm())
      }
    })
  }

  const hideSidebar = (e) => {
    dispatch(hideSidebarForm())
  }

  // based on https://gist.github.com/pstoica/4323d3e6e37e8a23dd59
  const onBlur = (e) => {
    const currentTarget = e.currentTarget

    // Check the newly focused element in the next tick of the event loop
    setTimeout(() => {
      // Check if the new activeElement is a child of the original container
      // Ignore clicks at menu button which reopens the form
      const el = document.activeElement
      const needHide = Boolean(
        !currentTarget.contains(el) &&
        !el.getAttribute('data-feedback-form-activator')
      )
      if (needHide) {
        hideSidebar()
      }
    }, 0)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      hideSidebar()
    }
  }

  const onShow = () => {
    focusTextArea()
  }

  useEffect(() => {
    if (isBecomeVisible) {
      onShow()
    }
  }, [isBecomeVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  const header = (
    <StyledHeader>
      <StyledCloseButton onClick={hideSidebar} iconName='icon-close-12' />
      <Title>{t('feedbackSidebarForm.title')}</Title>
      <Subtitle>{t('feedbackSidebarForm.subtitle1')}</Subtitle>
      <Subtitle>{t('feedbackSidebarForm.subtitle2')}</Subtitle>
    </StyledHeader>
  )

  return (
    <FeedbackPanelWrapper>
      <RightSidebar
        show={isFeedbackSidebarFormVisible}
        isPosAbsolute
        onClose={hideSidebar}
        title='title'
        headerComponent={header}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        tabIndex='0'
        dataTest='send-feedback-sidebar'
      >
        <Form
          onSubmit={onSubmit}
          subscription={{ invalid: true, pristine: true }}
        >
          {({ handleSubmit, pristine, form: form_, invalid }) => {
            const actionButtonDisabled = pristine || invalid || isCreating
            formRef.current = form_

            return (
              <>
                <SidebarSection title={t('feedbackSidebarForm.experienceSectionTitle')}>
                  <Field
                    name='experience'
                    component={EmoSwitcherAdapter}
                    initialValue='positive'
                  />
                </SidebarSection>

                <SidebarSection title={t('feedbackSidebarForm.descSectionTitle')} isRequired>
                  <Field
                    name='description'
                    component={DynamicTextAreaAdapter}
                    validate={requiredValidator}
                    domNodeRef={ref => (textareaRef.current = ref)}
                  />
                </SidebarSection>

                <SidebarSectionFormSubmit
                  withRequiredLegend
                  disabled={actionButtonDisabled}
                  onClick={handleSubmit}
                  buttonLabel={t('feedbackSidebarForm.buttonLabel')}
                />
              </>
            )
          }}
        </Form>
      </RightSidebar>
    </FeedbackPanelWrapper>
  )
}

export default FeedbackSidebarForm
